import { useQuery } from '@tanstack/react-query'
import { Eye, ChevronLeft, Crown } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { profileApi } from '../../api/profile'
import type { ProfileDTO } from '../../types'

export default function ViewersPage() {
  const navigate = useNavigate()

  const { data: viewers, isLoading, error } = useQuery({
    queryKey: ['profile-viewers'],
    queryFn: () => profileApi.getViewers().then((r) => r.data.data),
    retry: (n, e) => ((e as { response?: { status?: number } })?.response?.status === 403 ? false : n < 2),
  })
  const premiumGated = (error as { response?: { status?: number } })?.response?.status === 403

  return (
    <div className="max-w-2xl mx-auto px-4 py-5">
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-ivory-25 border border-ivory-200 shadow-soft flex items-center justify-center text-ink-400 hover:text-maroon-700"
        >
          <ChevronLeft size={20} />
        </button>
        <div>
          <h1 className="font-display font-semibold text-2xl text-ink-900">Who Viewed Me</h1>
          <p className="text-xs text-ink-400 mt-0.5">Profiles that viewed your profile recently</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 bg-ivory-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : premiumGated ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center shadow-glow">
            <Crown size={26} strokeWidth={1.75} className="text-white" />
          </div>
          <div>
            <p className="font-display font-semibold text-xl text-ink-900">See who viewed you</p>
            <p className="text-sm text-ink-400 mt-1 max-w-xs">
              Know who's interested — view everyone who visited your profile with a Premium membership.
            </p>
          </div>
          <Link to="/subscription" className="btn-primary text-sm py-2.5 px-6">Upgrade to Premium</Link>
        </div>
      ) : !viewers?.length ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-16 h-16 rounded-full bg-ivory-100 border border-gold-500/30 flex items-center justify-center">
            <Eye size={22} strokeWidth={1.5} className="text-gold-600/60" />
          </div>
          <p className="font-display font-semibold text-lg text-ink-900">No profile views yet</p>
          <p className="text-sm text-ink-400 text-center">When someone views your profile it will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {viewers.map((p: ProfileDTO) => (
            <Link
              key={p.id}
              to={`/profile/${p.id}`}
              className="flex items-center gap-4 card p-4 hover:shadow-card-hover transition-all"
            >
              <div className="w-14 h-14 rounded-full overflow-hidden bg-ivory-100 ring-1 ring-gold-500/40 flex-shrink-0">
                {p.avatarUrl ? (
                  <img src={p.avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display font-semibold text-xl text-maroon-700/40">
                    {p.fullName?.charAt(0) ?? '?'}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-ink-900 text-[16px] truncate">{p.fullName ?? 'Member'}</p>
                <p className="text-xs text-ink-400 truncate">
                  {[p.age && `${p.age} yrs`, p.nativeDistrict].filter(Boolean).join(' · ')}
                </p>
                {p.occupation && (
                  <p className="text-[11px] text-ink-400/80 truncate">{p.occupation.replace(/_/g, ' ')}</p>
                )}
              </div>
              <Eye size={15} strokeWidth={1.75} className="text-gold-600/50 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
